import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db, functions, httpsCallable } from '../../firebase';
import { getAuth } from 'firebase/auth';
import { toast } from 'react-toastify';

const AdminWelcomeEmail = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const usersRef = collection(db, 'users');
        const q = query(usersRef);
        const querySnapshot = await getDocs(q);
        
        const usersList = [];
        querySnapshot.forEach((doc) => {
          usersList.push({ id: doc.id, ...doc.data() });
        });
        
        setUsers(usersList);
      } catch (error) {
        console.error('Error fetching users:', error);
        toast.error('Failed to load users');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const handleSendWelcomeEmail = async () => {
    if (!selectedUser) {
      toast.error('Please select a user');
      return;
    }

    try {
      setLoading(true);
      const user = users.find(u => u.id === selectedUser);
      if (!user) {
        throw new Error('User not found');
      }

      // Get the Callable Function reference
      const sendWelcomeEmail = httpsCallable(functions, 'adminSendWelcomeEmailV2');
      
      // Call the function with user data
      const result = await sendWelcomeEmail({
        userId: user.id,
        email: user.email,
        customMessage: `Welcome to ASD Flo! We're excited to have you on board.`
      });
      
      console.log('Email sent successfully:', result.data);
      toast.success('Welcome email sent successfully!');
    } catch (error) {
      console.error('Error sending welcome email:', error);
      // Extract error message from the error object
      const errorMessage = error.message || 
                         error.details?.message || 
                         'Failed to send welcome email';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(user => 
    (user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.displayName?.toLowerCase().includes(searchTerm.toLowerCase())) &&
    user.email // Only include users with emails
  );

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Send Welcome Email</h1>
      
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Search Users
          </label>
          <input
            type="text"
            placeholder="Search by name or email..."
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select User
          </label>
          <select
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
            disabled={loading}
          >
            <option value="">Select a user...</option>
            {filteredUsers.map((user) => (
              <option key={user.id} value={user.id}>
                {user.displayName || 'No name'} - {user.email}
              </option>
            ))}
          </select>
        </div>

        <div className="flex justify-end">
          <button
            onClick={handleSendWelcomeEmail}
            disabled={!selectedUser || loading}
            className={`px-6 py-2 rounded-md text-white font-medium ${
              !selectedUser || loading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {loading ? 'Sending...' : 'Send Welcome Email'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminWelcomeEmail;
